import type { FC } from "react";
import { useEffect, useMemo, useState, type RefObject } from "react";
import { IconButton, Divider, Menu, MenuItem, Box, Button } from "@mui/material";
import { Translate, useTranslation } from "@privacy/translations";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { TrashCanIcon, CheckIcon, TableIcon, ChevronDownIcon } from "../shared/icons";
import * as FormDialog from "../shared/FormDialog";
import { ConfirmDeleteDialog } from "../shared/ConfirmDeleteDialog";
import { multilineEllipsis } from "../shared/multilineEllipsis";
import { HACK_alignIconWithLabel } from "@privacy/ui";
import type { GridApiPremium, GridFilterItem, GridInitialState } from "@mui/x-data-grid-premium";
import { useInitialisedGridApiRef } from "./DynamicGrid";

export type SavedFiltersProps = {
  onChange: (stringifiedFilters: string) => void;
  stringifiedFilters?: string;
  extraState?: Record<string, unknown>;
  onReset?: () => void;
};

export const SavedFilters: FC<SavedFiltersProps> = ({ onChange, stringifiedFilters, extraState, onReset }) => {
  const { t } = useTranslation();
  const gridApiRef = useInitialisedGridApiRef();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchorEl(null);

  const [filterIdToDelete, setFilterIdToDelete] = useState<null | string>(null);

  const [isNewFilterDialogOpen, setIsNewFilterDialogOpen] = useState(false);

  const { selected, filters } = useStringifiedFilters(gridApiRef, stringifiedFilters);

  const handleSave = (savedFilters: SavedFilters) => onChange(JSON.stringify(savedFilters));

  const handleResetSelectedToSaved = () => {
    handleCloseMenu();
    restoreSavedState(gridApiRef, selected.state);
    onReset?.();
  };

  const handleSaveChanges = () => {
    handleCloseMenu();
    handleSave({
      selectedId: selected.id,
      filters: filters.map(filter => ({
        ...filter,
        state: filter.id === selected.id ? { ...gridApiRef.current.exportState(), ...extraState } : filter.state,
      })),
    });
  };

  const handleSaveNew = (label: string) => {
    handleCloseMenu();
    setIsNewFilterDialogOpen(false);
    const newId = crypto.randomUUID();
    handleSave({
      selectedId: newId,
      filters: [...filters, { id: newId, label, state: { ...gridApiRef.current.exportState(), ...extraState } }],
    });
  };

  const handleDelete = () => {
    handleSave({
      selectedId: filterIdToDelete === selected.id ? null : selected.id,
      filters: filters.filter(filter => filter.id !== filterIdToDelete),
    });
    setFilterIdToDelete(null);
  };

  const handleSelectFilter = (id: SavedFilters["selectedId"]) => {
    handleCloseMenu();
    handleSave({ selectedId: id, filters });
  };

  return (
    <>
      <Button
        sx={{ px: 3, justifyContent: "start", width: 230, height: "100%" }}
        size="small"
        id="saved-filter-button"
        aria-controls={isMenuOpen ? "saved-filter-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? "true" : undefined}
        variant="outlined"
        onClick={handleOpenMenu}
        startIcon={<TableIcon size={14} />}
        endIcon={<ChevronDownIcon size={14} />}
      >
        <Box
          sx={{
            mr: "auto",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ...HACK_alignIconWithLabel,
          }}
        >
          {selected.label ?? t("DynamicFormGrid.SavedFilter.StandardFilterLabel")}
        </Box>
      </Button>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        slotProps={{
          list: { "aria-labelledby": "saved-filter-button" },
        }}
      >
        <Box sx={{ minWidth: 300, maxWidth: 400 }}>
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            <MenuItem onClick={() => handleSelectFilter(null)}>
              {!selected.id ? <CheckIcon size={14} /> : <HiddenCheckIcon />}
              {t("DynamicFormGrid.SavedFilter.StandardFilterLabel")}
            </MenuItem>

            {filters.map(({ id, label }) => (
              <MenuItem key={id} onClick={() => handleSelectFilter(id)}>
                {selected.id === id ? <CheckIcon /> : <HiddenCheckIcon />}
                <Box sx={multilineEllipsis(1)}>{label}</Box>
                <IconButton
                  size="small"
                  aria-label={t("DynamicFormGrid.SavedFilter.Delete")}
                  sx={{ ml: "auto" }}
                  onClick={e => {
                    e.stopPropagation();
                    setFilterIdToDelete(id);
                  }}
                >
                  <TrashCanIcon size={14} />
                </IconButton>
              </MenuItem>
            ))}
          </Box>

          <Divider />
          <MenuItem onClick={handleResetSelectedToSaved}>
            <HiddenCheckIcon />
            <Box>
              <Translate
                translationKey="DynamicFormGrid.SavedFilter.ResetToSaved"
                values={{
                  name: (
                    <Box
                      component="span"
                      sx={{ fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {selected.label ?? t("DynamicFormGrid.SavedFilter.StandardFilterLabel")}
                    </Box>
                  ),
                }}
              />
            </Box>
          </MenuItem>
          <MenuItem disabled={selected.id === null} onClick={handleSaveChanges}>
            <HiddenCheckIcon />
            {t("DynamicFormGrid.SavedFilter.SaveChanges")}
          </MenuItem>
          <MenuItem onClick={() => setIsNewFilterDialogOpen(true)}>
            <HiddenCheckIcon />
            {t("DynamicFormGrid.SavedFilter.SaveAsNew")}
          </MenuItem>
        </Box>
      </Menu>
      {isNewFilterDialogOpen && (
        <NewFilterDialog
          existingFilterLabels={filters.map(filter => filter.label)}
          onCancel={() => setIsNewFilterDialogOpen(false)}
          onSubmit={({ label }) => handleSaveNew(label)}
        />
      )}
      {Boolean(filterIdToDelete) && (
        <ConfirmDeleteDialog
          isOpen
          id="delete-filter-dialog"
          onDelete={async () => handleDelete()}
          onClose={() => setFilterIdToDelete(null)}
          headerText={t("DynamicFormGrid.SavedFilter.ConfirmDelete.Header")}
          message={(() => {
            const filter = filters.find(({ id }) => id === filterIdToDelete);
            if (!filter) return null;
            return (
              <Translate
                translationKey="DynamicFormGrid.SavedFilter.ConfirmDelete.Message"
                values={{
                  name: (
                    <Box component="span" sx={{ fontWeight: "600" }}>
                      {filter.label}
                    </Box>
                  ),
                }}
              />
            );
          })()}
          confirmButtonLabel={t("DynamicFormGrid.SavedFilter.ConfirmDelete.Submit")}
        />
      )}
    </>
  );
};

/** Used to take up the same space as the check icon */
const HiddenCheckIcon = () => (
  <Box component="span" sx={{ visibility: "hidden" }}>
    <CheckIcon size={14} />
  </Box>
);

const useStringifiedFilters = (gridApiRef: RefObject<GridApiPremium>, stringifiedFilters?: string) => {
  const savedFilters: SavedFilters = useMemo(() => parseStringifiedFilters(stringifiedFilters), [stringifiedFilters]);
  const [defaultState] = useState(() => gridApiRef.current.exportState());

  const defaultFilter = useMemo(
    () =>
      ({
        label: null,
        state: defaultState,
        id: null,
      }) as const,
    [defaultState]
  );

  const selected = savedFilters.filters.find(filter => filter.id === savedFilters.selectedId) ?? defaultFilter;

  useEffect(() => restoreSavedState(gridApiRef, selected.state), [gridApiRef, selected]);

  return { selected, filters: savedFilters.filters } as const;
};

const hasFilterOperator = (gridApiRef: RefObject<GridApiPremium>, item: GridFilterItem) =>
  Boolean(
    gridApiRef.current.getColumn(item.field)?.filterOperators?.some(operator => operator.value === item.operator)
  );

const restoreSavedState = (gridApiRef: RefObject<GridApiPremium>, state: GridInitialState) => {
  const filterModel = state?.filter?.filterModel;

  gridApiRef.current.restoreState(
    filterModel
      ? {
          ...state,
          filter: {
            ...state.filter,
            filterModel: {
              ...filterModel,
              items: filterModel.items.filter(item => hasFilterOperator(gridApiRef, item)),
            },
          },
        }
      : state
  );
};

export const selectedSavedFilterId = (stringifiedFilters?: string) =>
  parseStringifiedFilters(stringifiedFilters).selectedId ?? null;

export const selectedSavedFilterState = (stringifiedFilters?: string): unknown => {
  const { selectedId, filters } = parseStringifiedFilters(stringifiedFilters);

  return filters.find(filter => filter.id === selectedId)?.state;
};

export const selectDefaultSavedFilter = (stringifiedFilters?: string) =>
  JSON.stringify({ ...parseStringifiedFilters(stringifiedFilters), selectedId: null });

const parseStringifiedFilters = (stringifiedFilters = ""): SavedFilters => {
  try {
    return SavedFiltersSchema.parse(JSON.parse(stringifiedFilters));
  } catch {
    return SavedFiltersSchema.parse(null);
  }
};

const FiltersSchema = z.object({ id: z.string(), label: z.string(), state: z.any() });

const SavedFiltersSchema = z
  .object({
    selectedId: z.string().nullish(),
    /** If any filter is not valid we just ignore it */
    filters: z.preprocess(input => {
      return z
        .array(z.any())
        .parse(input)
        .filter(filter => FiltersSchema.safeParse(filter).success);
    }, z.array(FiltersSchema)),
  })
  .catch({ selectedId: null, filters: [] });

type SavedFilters = z.infer<typeof SavedFiltersSchema>;

/** NewFilterDialog */
type NewFilterDialogProps = {
  existingFilterLabels: string[];
  onCancel: () => void;
  onSubmit: (data: { label: string }) => void;
};
type NewFilterFormValues = { label: string };

const NewFilterDialog: FC<NewFilterDialogProps> = ({ onCancel, onSubmit, existingFilterLabels }) => {
  const { t } = useTranslation();

  const { handleSubmit, reset, control, setFocus } = useForm<NewFilterFormValues>({
    defaultValues: { label: "" },
  });

  FormDialog.useAutoFocusField(setFocus, "label");

  const handleCancel = () => {
    onCancel();
    reset();
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("DynamicFormGrid.SavedFilter.NewDialog.Header")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(onSubmit)}>
        <FormDialog.Content>
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="label"
              rules={{
                required: t("Input.Validation.Required"),
                validate: value =>
                  existingFilterLabels.includes(value)
                    ? t("DynamicFormGrid.SavedFilter.NewDialog.NameAlreadyExistsError")
                    : true,
              }}
              label={t("DynamicFormGrid.SavedFilter.NewDialog.NameLabel")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleCancel} />
          <FormDialog.SubmitButton>{t("DynamicFormGrid.SavedFilter.NewDialog.SubmitLabel")}</FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
